import {
  MeshBasicMaterial,
  Mesh,
  Shape,
  Path,
  ShapeGeometry,
  PlaneGeometry,
} from "three";
import { VBase } from "./VBase";
import VUtil from "../VUtils";

const defaultOptions = {
  text: "Toggle",
  color: 0xffffff,
  textSize: 0.05,
  textColor: 0x000000,
  width: 0.08,
  height: 0.08,
  font: "normal",
  defaultValue: false,
  disabledColor: 0xc8c8c8,
};

/*
  使用案例

      let toggle = this.uiLib.createToggle({
        defaultValue: false,
        text: "音效",
      });
      toggle.addOnValueChangeEvent((value) => {
        console.log("选择的开关", value);
      });
      this.uiLib.addUIToScene(toggle);


*/
export class VToggle extends VBase {
  /**
   * options:
   * {
   *      priority: 优先级，默认为0，值越小越靠前
   *      renderOrder: 渲染顺序，默认为99999
   *      text: 文字内容，默认为"Toggle"
   *      color: 颜色。默认为 0xffffff
   *      textSize: 文字大小，默认为 0.03
   *      textColor: 文字颜色，默认为 0x000000
   *      width: 按钮宽度，默认为 0.08
   *      height: 按钮高度，默认为 0.08
   *      defaultValue: 默认数值，布尔值，true表示打开，false表示关闭，默认为false
   *      font: 字体正常或粗体，"normal"-正常  "bold"-粗体 ，默认为"normal"
   *      disabledColor:  ui对象禁用颜色，默认为 0xC8C8C8
   * }
   * @param {*} options
   */
  constructor(options, vuiLib) {
    if (!options) options = {};

    const tempOptions = {
      priority: options.priority !== undefined ? options.priority : 0,
      renderOrder:
        options.renderOrder !== undefined ? options.renderOrder : 99999,
      opacity: 0.01,
      text: options.text ? options.text : defaultOptions.text,
      color: options.color !== undefined ? options.color : defaultOptions.color,
      textSize:
        options.textSize !== undefined
          ? options.textSize
          : defaultOptions.textSize,
      textColor:
        options.textColor !== undefined
          ? options.textColor
          : defaultOptions.textColor,
      width: options.width !== undefined ? options.width : defaultOptions.width,
      height:
        options.height !== undefined ? options.height : defaultOptions.height,
      font: options.font ? options.font : defaultOptions.font,
      defaultValue:
        options.defaultValue !== undefined
          ? options.defaultValue
          : defaultOptions.defaultValue,
      disabledColor:
        options.disabledColor !== undefined
          ? options.disabledColor
          : defaultOptions.disabledColor,
    };

    super(tempOptions, vuiLib);
    this._vType = "VToggle";

    this._otherProps = {
      onValueChangeEvent: [],
    };

    const offset = 0.01;
    this.value = tempOptions.defaultValue ? true : false;

    this.name = "VToggle_" + this._textProps.text;

    {
      const geo = this._createHollowRectangleGeometry(
        this._baseProps.width,
        this._baseProps.height,
        offset
      );
      const mat = new MeshBasicMaterial({
        color: this._baseProps.color,
        depthTest: this.depthTest,
      });
      VUtil.convertSRGBToLinear(mat);
      const outShape = new Mesh(geo, mat);
      outShape.renderOrder = this._baseProps.renderOrder;
      this.add(outShape);
    }

    {
      const geo = new PlaneGeometry(
        this._baseProps.width - offset * 3,
        this._baseProps.height - offset * 3
      );
      const mat = new MeshBasicMaterial({
        color: this._baseProps.color,
        depthTest: this.depthTest,
      });
      VUtil.convertSRGBToLinear(mat);
      this.innerShaper = new Mesh(geo, mat);
      this.innerShaper.renderOrder = this._baseProps.renderOrder;
      this.add(this.innerShaper);
      this.innerShaper.visible = this.value;
    }

    // 文字
    const text = VUtil.createText(
      this._textProps.text,
      this._textProps.textSize,
      this._textProps.textColor,
      this._textProps.font,
      (tex) => {
        tex.position.x += tex._width / 2 + this._baseProps.width;

        this.width += tex._width + this._baseProps.width;
      }
    );

    text.renderOrder = this._baseProps.renderOrder;
    this.add(text);
  }

  onSelectEnd(obj) {
    if (!this._baseProps || !this._baseProps.enabled) return;
    this.value = !this.value;
    this.innerShaper.visible = this.value;

    for (var i = 0; i < this._otherProps.onValueChangeEvent.length; i++) {
      this._otherProps.onValueChangeEvent[i](this.value);
    }

    super.onSelectEnd(obj);
  }

  /**
   * 创建镂空矩形几何体
   * @param {Number} width 外围宽度
   * @param {Number} height 外围高度
   * @param {Number} gap 内外间距
   */
  _createHollowRectangleGeometry(width, height, gap) {
    // 创建外部矩形形状
    const outerShape = new Shape();
    outerShape.moveTo(-width / 2, -height / 2); // 起点
    outerShape.lineTo(width / 2, -height / 2); // 右
    outerShape.lineTo(width / 2, height / 2); // 上
    outerShape.lineTo(-width / 2, height / 2); // 左
    outerShape.lineTo(-width / 2, -height / 2); // 下

    // 创建内部矩形形状（镂空部分）
    const innerShape = new Path();
    innerShape.moveTo(-(width / 2 - gap), -(height / 2 - gap)); // 起点
    innerShape.lineTo(width / 2 - gap, -(height / 2 - gap)); // 右
    innerShape.lineTo(width / 2 - gap, height / 2 - gap); // 上
    innerShape.lineTo(-(width / 2 - gap), height / 2 - gap); // 左
    innerShape.lineTo(-(width / 2 - gap), -(height / 2 - gap)); // 下

    // 将内部形状添加到外部形状的 holes 属性中
    outerShape.holes.push(innerShape);

    // 创建几何体
    const geometry = new ShapeGeometry(outerShape);

    return geometry;
  }

  /**
   * 添加一个颜色修改回调事件，回调事件参数为 颜色值，如"#ffffff"
   * @param {*} fun
   * @returns
   */
  addOnValueChangeEvent(fun) {
    if (!fun) return;
    this._otherProps.onValueChangeEvent.push(fun);
  }

  /**
   * 移除一个颜色修改回调事件
   * @param {*} fun
   * @returns
   */
  removeOnValueChangeEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onValueChangeEvent.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onValueChangeEvent.splice(index, 1);
    }
  }

  /**
   * 移除所有颜色修改回调事件
   */
  removeAllOnValueChangeEvent() {
    this._otherProps.onValueChangeEvent = [];
  }
}
